import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, getTicketTypeById, getTicketTypeInfosByTicketTypeId } from "ticketino-api-client";
import parse from "html-react-parser";

import Footer from "./components/Footer";

const Checkout = () => {
    const [orderId] = useState(sessionStorage.getItem("OrderId"));
    const [token] = useState(sessionStorage.getItem("token"));

    const [order, setOrder] = useState({});
    const [tickets, setTickets] = useState([]);
    const [payment, setPayment] = useState({
        paymentMethodId: 0,
        deliveryMethodId: 0,
        insurance: false
    })

    const [submitError, setSubmitError] = useState("");

    const theEvent = JSON.parse(sessionStorage.getItem("FUWEvent"));
    const [orderInsurancePrice, setOrderInsurancePrice] = useState(0);

    // react hook for navigation
    let navigate = useNavigate();

    // base url
    let baseUrl = process.env.REACT_APP_BASEURL_API;

    // setting the base url of the npm package api calls
    api.defaults.baseURL = baseUrl;

    // fetching resources
    const [resources, setResources] = useState({});

    // fetching params
    const { language } = useParams();

    let languageId = 0;

    // changing languageId according to the url
    switch (language) {
        case ("de" || "DE"):
            languageId = 1;
            break;
        case ("fr" || "FR"):
            languageId = 2;
            break;
        case ("en" || "EN"):
            languageId = 3;
            break;
        case ("it" || "IT"):
            languageId = 4;
            break;
        default:
            languageId = 0;
            break;
    }

    useEffect(() => {
        // gtm - addPaymentInfo
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'addPaymentInfo'
        });
    }, [])

    useEffect(() => {
        requestResources();
    }, [language]); //everytime language is changed

    useEffect(() => {
        axios.defaults.headers.common["Authorization"] = "Bearer " + token;
        api.defaults.headers.common['Authorization'] = "Bearer " + token;

        if (orderId) {
            loadOrder(orderId);
            getOrderInsurance();
        }
    }, [orderId]); //gets called when an order is started

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.checkOut);
            })
            .catch((error) => console.error(error.response.data));
    };

    const loadOrder = async (orderId) => {
        try {
            // Order
            const updatedOrder = await getOrderByOrderId(orderId);

            const bookedTickets = updatedOrder.tickets;

            // Booked Tickets
            const updatedTickets = await Promise.all(
                bookedTickets.map(async (ticket) => {
                    const ticketType = await getTicketTypeById(ticket.ticketTypeId);

                    const info = await getTicketTypeInfosByTicketTypeId(ticket.ticketTypeId);
                    const ticketTypeInfo = info.find(info => info.languageId == languageId) ?? info[0];

                    return { ...ticket, ticketType: ticketType, info: ticketTypeInfo }
                })
            )

            let updatedPayment = { ...payment, deliveryMethodId: updatedOrder.deliveryMethodId, paymentMethodId: updatedOrder.paymentMethodId };

            updatedPayment.insurance = updatedOrder.totalInsuranceAmount > 0;

            setPayment(updatedPayment);
            setOrder(updatedOrder);
            setTickets(updatedTickets);
        }
        catch (error) {
            console.error(error);
        }
    }

    const getOrderByOrderId = async (orderId) => {
        try {
            const res = await axios.get(`${baseUrl}/ShopBasket/Order/${orderId}`);
            return res.data;
        }
        catch (error) {
            console.error(error);
        }
    }

    const changeDeliveryType = async (deliveryMethodId) => {
        await axios.put(
            `${baseUrl}/ShopBasket/Order/${orderId}/DeliveryMethod/${deliveryMethodId}`)
            .then(() => {
                loadOrder(orderId);
            })
            .catch((error) => {
                console.error(error.response.data);
            });
    };

    const changePaymentType = (paymentMethodId) => {
        axios
            .put(`${baseUrl}/ShopBasket/Order/${orderId}/PaymentType/${paymentMethodId}`)
            .then((res) => {
                loadOrder(orderId);
            })
            .catch((error) => {
                console.error(error.response.data);
            });
    };

    const startDatatrans = () => {
        var baseRequestUrl = `https://datatrans.ticketino.com/Datatrans/${orderId}/DigitalSignature`;

        axios
            .get(baseRequestUrl)
            .then((res) => {
                let datatransFormId = "datatrans-" + new Date().getTime();
                var form =
                    "<form className='datatrans' id='" + datatransFormId + "'></form>";

                let container = document.getElementById("datatrans-form-placeholder");

                container.innerHTML += form;

                let element = document.getElementById(datatransFormId);

                // merchantId for testing
                // element.setAttribute("data-merchant-id", "1100004624");
                element.setAttribute("data-merchant-id", res.data.merchantId);
                element.setAttribute("data-amount", res.data.amount);
                element.setAttribute("data-currency", res.data.currency);
                element.setAttribute("data-refno", res.data.referenceNumber);
                element.setAttribute("data-reqType", res.data.reqType);
                element.setAttribute(
                    "data-upp-return-target",
                    res.data.uppReturnTarget
                );
                element.setAttribute("data-paymentMethod", res.data.paymentMethod);
                element.setAttribute("data-sign", res.data.digitalSignature);

                let domain = "https://" + window.location.host;

                let successUrl = domain + `/form/redirect/${language}/success/${orderId}`;
                let errorUrl = domain + `/form/redirect/${language}/error/${orderId}`;
                let cancelUrl = domain + `/form/redirect/${language}/cancel/${orderId}`;

                element.setAttribute("data-success-url", successUrl);
                element.setAttribute("data-error-url", errorUrl);
                element.setAttribute("data-cancel-url", cancelUrl);

                for (const key in res.data.userInfo) {
                    element.setAttribute(key, res.data.userInfo[key]);
                }

                for (const key in res.data.merchantSpecificParameters) {
                    element.setAttribute(key, res.data.merchantSpecificParameters[key]);
                }

                // start datatrans -> call the payment form
                window.Datatrans.startPayment({
                    form: "#" + datatransFormId,
                });
            })
            .catch((error) => {
                alert(error.response.data);
            });
    };

    // validate that the order still exists before payment process
    const validateOrderStillExists = async () => {
        try {
            const latest = await getOrderByOrderId(orderId);
            if (!latest) return false;
            if (!latest?.id) return false;
            if (!Array.isArray(latest?.tickets) || latest.tickets.length === 0) return false;
            return true;
        } catch (e) {
            return false;
        }
    }

    const onSubmit = async () => {

        setSubmitError("");

        const exists = await validateOrderStillExists();
        if (!exists) {
            setSubmitError(resources?.OrderExpiredMessage || "Your reservation is no longer available. Please start again.");
            return;
        }

        if (order?.totalPrice > 0) {
            let dataTransProviders = [1, 7, 8, 9, 10, 14, 11];

            let datatransProvider = dataTransProviders.findIndex((d) => d === payment.paymentMethodId) !== -1

            if (datatransProvider) {
                startDatatrans();
            } else {
                navigate(`/${language}/confirmation/${orderId}`);
            }
        } else {
            changePaymentTypeToFree();
        }
    }

    const getOrderInsurance = async () => {
        try {
            const res = await axios.get(`${baseUrl}/ShopBasket/Order/${orderId}/Insurance`);
            setOrderInsurancePrice(res.data);
        } catch (error) {
            console.error(error);
        }
    }

    const setOrderInsurance = async () => {
        await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/Insurance`)
            .then(() => {
                loadOrder(orderId);
            }).catch((error) => {
                console.error(error);
            })
    }

    const removeOrderInsurance = async () => {
        await axios.delete(`${baseUrl}/ShopBasket/Order/${orderId}/Insurance`)
            .then(() => {
                loadOrder(orderId);
            }).catch((error) => {
                console.error(error);
            })
    }

    const confirmShopbasketByOrderId = async (orderId) => {
        try {
            const res = await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/Confirm`);
            return res.data;
        } catch (error) {
            console.error(error)
        }
    };

    const changePaymentTypeToFree = () => {
        axios
            .put(`${baseUrl}/ShopBasket/Order/${orderId}/PaymentType/5`)
            .then(async () => {
                await confirmShopbasketByOrderId(orderId);
                navigate(`/${language}/confirmation/${orderId}`);
            });
    }

    console.log(order);

    const mapTickets = () => {
        let ticketsSorted = [];

        if (tickets && tickets.length > 0) {
            ticketsSorted = [...tickets].sort((a, b) => a.id - b.id);
        }

        return (
            tickets &&
            tickets.length > 0 &&
            ticketsSorted.map((tt, index) => (
                <div key={index}>
                    <div className="row pt-2 pb-2">
                        <div className="col-md-10 col-8 p-0">
                            <label className="fs-6">
                                {tt.info?.name}
                            </label>
                        </div>
                        <div className="col-md-2 col-4 text-end p-0">
                            <label>
                                {tt.ticketType?.currency} {tt.price.toFixed(2)}
                            </label>
                        </div>
                    </div>
                </div>
            ))
        );
    }

    const mapExtraCosts = () => {
        return (
            <div>
                <div className="row pt-2 pb-2">
                    <div className="col-md-10 col-8 p-0">
                        <label className="fs-6">
                            Kosten für Lieferung und Zahlung
                        </label>
                    </div>
                    <div className="col-md-2 col-4 text-end p-0">
                        <label>
                            {order.currency + " " + order?.deliverPaymentFee.toFixed(2)}
                        </label>
                    </div>
                </div>
            </div>
        )
    }

    const mapInsurance = () => {
        return (
            <div>
                <div className="row pt-2 pb-2">
                    <div className="col-md-10 col-8 p-0">
                        <label className="fs-6">
                            {resources.TicketInsurance}
                        </label>
                    </div>
                    <div className="col-md-2 col-4 text-end p-0">
                        <label>
                            {order.currency + " " + order.totalInsuranceAmount.toFixed(2)}
                        </label>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div>
            <div id="datatrans-form-placeholder"></div>
            <div className="container pt-0 wrapper-checkout-page">
                <>
                    <div className="row mt-4">
                        <div className="col-md-12">
                            <h2>{resources.Title}</h2>
                        </div>
                    </div>
                    <div className="mt-5">
                        <div className="row">
                            <div className="col-md-12 mb-2">
                                {theEvent?.noPostDelivery === false ? (
                                    <h1 className="fs-5 red">{resources.Delivery}</h1>
                                ) : (
                                    <h1 className="fs-5 red">{resources.DeliverySingle}</h1>
                                )}
                            </div>
                        </div>
                        <div className="mb-5 text-center check-page-box">
                            <div className="mb-3">
                                <input
                                    className="me-3"
                                    type="radio"
                                    name="flexRadioDefault1"
                                    checked={payment.deliveryMethodId === 1 ? true : false}
                                    onClick={() => changeDeliveryType(1)}
                                    id="printAtHome"
                                />
                                <label
                                    className="form-check-label fs-6 bold"
                                    htmlFor="printAtHome"
                                >
                                    Print At Home
                                </label>
                                <p className="pt-1 m-0">
                                    {resources.DeliveryNoFee}
                                </p>
                            </div>
                            {theEvent?.noPostDelivery === false &&
                                <div className="mb-3 mt-4">
                                    <input
                                        className="me-3"
                                        type="radio"
                                        name="flexRadioDefault1"
                                        checked={payment.deliveryMethodId === 2 ? true : false}
                                        onClick={() => changeDeliveryType(2)}
                                        id="postMail"
                                    />
                                    <label
                                        className="form-check-label fs-6 bold"
                                        htmlFor="postMail"
                                    >
                                        {resources.PostDelivery}
                                    </label>
                                    <p className="pt-1 m-0">
                                        {resources.DeliveryFee}
                                    </p>
                                </div>
                            }
                        </div>
                    </div>
                    <div>
                        {order.totalPrice > 0 && <>
                            <div className="row">
                                <div className="col-md-12 mb-2">
                                    <h1 className="fs-5 red">{resources.Payment}</h1>
                                </div>
                            </div>
                            <div className="mb-4 text-center check-page-box">
                                <div className="mb-3">
                                    <input
                                        className="me-3"
                                        type="radio"
                                        name="flexRadioDefault"
                                        checked={payment.paymentMethodId === 2 ? true : false}
                                        onClick={() => changePaymentType(2)}
                                        id="invoice"
                                    />
                                    <label
                                        className="form-check-label fs-6 bold"
                                        htmlFor="invoice"
                                    >
                                        {resources.PaymmentInvoice}
                                    </label>
                                    <p className="pt-1 m-0"> {resources.PaymentSmallFee}</p>
                                </div>
                                {/*<div className="mb-3 mt-4">*/}
                                {/*    <input*/}
                                {/*        className="me-3"*/}
                                {/*        type="radio"*/}
                                {/*        name="flexRadioDefault"*/}
                                {/*        checked={payment.paymentMethodId === 11 ? true : false}*/}
                                {/*        onClick={() => changePaymentType(11)}*/}
                                {/*        id="sofort"*/}
                                {/*    />*/}
                                {/*    <label*/}
                                {/*        className="form-check-label fs-6 bold"*/}
                                {/*        htmlFor="sofort"*/}
                                {/*    >*/}
                                {/*        {resources.PaymentSofort}*/}
                                {/*    </label>*/}
                                {/*    <p className="pt-1 m-0">{resources.PaymentNoFees}</p>*/}
                                {/*</div>*/}
                                {/*<div className="mb-3 mt-4">*/}
                                {/*    <input*/}
                                {/*        className="me-3"*/}
                                {/*        type="radio"*/}
                                {/*        name="flexRadioDefault"*/}
                                {/*        checked={payment.paymentMethodId === 10 ? true : false}*/}
                                {/*        onClick={() => changePaymentType(10)}*/}
                                {/*        id="paypal"*/}
                                {/*    />*/}
                                {/*    <label*/}
                                {/*        className="form-check-label fs-6 bold"*/}
                                {/*        htmlFor="paypal"*/}
                                {/*    >*/}
                                {/*        PayPal*/}
                                {/*    </label>*/}
                                {/*    <p className="pt-1 m-0">*/}
                                {/*        Zusatzgebühr von CHF + 4.9%; EUR + 3.9%.*/}
                                {/*    </p>*/}
                                {/*</div>*/}
                                <div className="mb-3 mt-4">
                                    <input
                                        className="me-3"
                                        type="radio"
                                        name="flexRadioDefault"
                                        checked={payment.paymentMethodId === 1 ? true : false}
                                        onClick={() => changePaymentType(1)}
                                        id="mastercard"
                                    />
                                    <label
                                        className="form-check-label fs-6 bold"
                                        htmlFor="mastercard"
                                    >
                                        Mastercard
                                    </label>
                                    <p className="pt-1 m-0">{resources.PaymentNoFees}</p>
                                </div>
                                <div className="mb-3 mt-4">
                                    <input
                                        className="me-3"
                                        type="radio"
                                        name="flexRadioDefault"
                                        checked={payment.paymentMethodId === 7 ? true : false}
                                        onClick={() => changePaymentType(7)}
                                        id="visa"
                                    />
                                    <label
                                        className="form-check-label fs-6 bold"
                                        htmlFor="visa"
                                    >
                                        Visa
                                    </label>
                                    <p className="pt-1 m-0">{resources.PaymentNoFees}</p>
                                </div>
                                <div className="mb-3 mt-4">
                                    <input
                                        className="me-3"
                                        type="radio"
                                        name="flexRadioDefault"
                                        checked={payment.paymentMethodId === 8 ? true : false}
                                        onClick={() => changePaymentType(8)}
                                        id="amex"
                                    />
                                    <label
                                        className="form-check-label fs-6 bold"
                                        htmlFor="amex"
                                    >
                                        American Express
                                    </label>
                                    <p className="pt-1 m-0">{resources.PaymentNoFees}</p>
                                </div>
                                <div className="mb-3 mt-4">
                                    <input
                                        className="me-3"
                                        type="radio"
                                        name="flexRadioDefault"
                                        checked={payment.paymentMethodId === 9 ? true : false}
                                        onClick={() => changePaymentType(9)}
                                        id="postfinance"
                                    />
                                    <label
                                        className="form-check-label fs-6 bold"
                                        htmlFor="postfinance"
                                    >
                                        PostFinance
                                    </label>
                                    <p className="pt-1 m-0">
                                        {resources.PaymentFee1}
                                    </p>
                                </div>
                                <div className="mb-3 mt-4">
                                    <input
                                        className="me-3"
                                        type="radio"
                                        name="flexRadioDefault"
                                        checked={payment.paymentMethodId === 14 ? true : false}
                                        onClick={() => changePaymentType(14)}
                                        id="twint"
                                    />
                                    <label
                                        className="form-check-label fs-6 bold"
                                        htmlFor="twint"
                                    >
                                        Twint
                                    </label>
                                    <p className="pt-1 m-0">
                                        {resources.PaymentNoFees}
                                    </p>
                                </div>
                            </div>
                        </>
                        }

                        {order.totalPrice > 0 && orderInsurancePrice > 0 &&
                            <div className="row">
                                <div className="col-md-12 mb-2">
                                    <h1 className="fs-5 red">{resources.TicketInsurance}</h1>
                                </div>
                                <div className="col-md-12 mb-3">
                                    <div className="form-check mb-4">
                                        <input
                                            className="form-check-input me-3 "
                                            type="checkbox"
                                            checked={payment.insurance}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setOrderInsurance();
                                                } else {
                                                    removeOrderInsurance();
                                                }
                                            }}
                                        />
                                        <label className="col-11 form-check-label fs-6" htmlFor="inlineCheckbox1">
                                            {parse(
                                                (orderInsurancePrice === 0
                                                    ? resources.TicketInsuranceDescGeneric
                                                    : (resources.TicketInsuranceDesc ?? "").replace("{@InsurancePrice}", orderInsurancePrice.toFixed(2))
                                                ) ?? ""
                                            )}
                                        </label>
                                    </div>

                                </div>
                            </div>}

                        <div className="row">
                            <div className="col-md-12 mb-2">
                                <h1 className="fs-5 red">{resources.Summary}</h1>
                            </div>
                            <div className="col-md-12 mb-1">
                                <div className="container">
                                    {mapTickets()}
                                    {payment.insurance && mapInsurance()}
                                    {order.deliverPaymentFee > 0 && mapExtraCosts()}
                                    <hr className="custom-hr"></hr>
                                    <div className="row mb-3">
                                        <div className="col-md-10 col-8 p-0">
                                            <label className="fs-6 fw-bold">
                                                {resources.Total}
                                            </label>
                                        </div>
                                        <div className="col-md-2 col-4 text-end p-0">
                                            <label className="fw-bold">
                                                {order.currency} {order.totalPrice?.toFixed(2)}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>

                        <div className="row mt-5">
                            <div className="offset-md-8 col-md-4 text-end">
                                <button
                                    className="btn form-control btnDarkblue"
                                    onClick={() => onSubmit()}
                                    disabled={
                                        payment.paymentMethodId === 0 && payment.deliveryMethodId === 0
                                    }
                                >
                                    {resources.FinishOrder}
                                </button>
                            </div>
                        </div>

                        {submitError && (
                            <div className="row mt-3">
                                <div className="col-12">
                                    <div className="alert alert-danger" role="alert">
                                        {submitError}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                </>
            </div>
            {/* footer */}
            <div className="mt-5 footerBgColor">
                <div className="container wrapper">
                    <div className="row">
                        <div className="col-md-12 text-center">
                            <Footer language={language} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Checkout